/**
 * Hint — the tooltip of one control: the action of a control that shows only
 * an icon, or the cause that keeps a control disabled. Rules 6 and 7 of
 * docs/rules/interactive_elements.md.
 *
 * The tooltip stays mounted while the label is empty, so a control that gains
 * or loses its hint (a Save button that becomes disabled) keeps its DOM node
 * and its focus. Without a label the tooltip is inert, so it does not close
 * the tooltips inside it. A disabled <button> still gets pointer events, only
 * no clicks, so the hint opens on it directly.
 */

import * as React from 'react';

import { TooltipMode } from '@/types/tooltip';

import { Tooltip } from './ui/Tooltip';
import { TooltipContent } from './ui/TooltipContent';
import { TooltipTrigger } from './ui/TooltipTrigger';

type Side = React.ComponentProps<typeof TooltipContent>['side'];

interface Props {
    /** The text of the tooltip. Empty or absent: no tooltip opens. */
    label?: string;
    side?: Side;
    /** One element that forwards its ref and its props to a DOM node. */
    children: React.ReactElement;
}

export const Hint = ({ label, side, children }: Props): React.JSX.Element => {
    const isShown = label !== undefined && label !== '';
    return (
        <Tooltip mode={isShown ? TooltipMode.Hover : TooltipMode.Inert}>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent side={side}>{label}</TooltipContent>
        </Tooltip>
    );
};
